import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchDesigns,
  createDesign,
  updateDesign,
  deleteDesign,
} from "../api/DesignAPIs";
import {
  fetchCategories,
  fetchCategoryById,
  fetchDesignsForCategory,
} from "../api/CategoryAPIs";
import { colors } from "../constants/colors";
import { API_BASE_URL_V2 } from "../api/URL_Header";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(5)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-20 h-20 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-3">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const DesignsPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [designs, setDesigns] = useState([]);
  const [newDesign, setNewDesign] = useState(null);
  const [editingDesign, setEditingDesign] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50; // يمكنك تغييره إلى 100 لو عايز
  const [totalDesigns, setTotalDesigns] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Fetch categories (for dropdown)
  const {
    data: categoryData,
    isLoading: isLoadingCategories,
    error: errorCategories,
  } = useQuery({
    queryKey: categoryId ? ["category", categoryId] : ["categories"],
    queryFn: categoryId ? () => fetchCategoryById(categoryId) : fetchCategories,
    staleTime: Infinity,
  });

  // Fetch designs with pagination
  const {
    data: designData,
    isLoading: isLoadingDesigns,
    isFetching,
    error: errorDesigns,
  } = useQuery({
    queryKey: ["designs", categoryId || "all", currentPage, pageSize],
    queryFn: categoryId
      ? () => fetchDesignsForCategory(categoryId, currentPage, pageSize)
      : () => fetchDesigns(currentPage, pageSize),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const isLoading = isLoadingCategories || isLoadingDesigns;
  const error = errorCategories || errorDesigns;

  // Load More function
  const loadMoreDesigns = () => {
    if (!isLoadingMore && hasMore) {
      setIsLoadingMore(true);
      setCurrentPage((prev) => prev + 1);
    }
  };

  // Update designs when new data arrives
  useEffect(() => {
    if (designData && categoryData) {
      const newDesigns = (designData.designs || []).map((design) => ({
        id: design.designId,
        name: design.name || "Unnamed Product",
        imageUrl_1: design.imageUrl_1 || "",
        imageUrl_2: design.imageUrl_2 || "",
        newPrice: design.priceAfter || 0,
        oldPrice: design.priceBefore || 0,
        inStock: design.inStock !== undefined ? design.inStock : true,
        hasOffer: design.hasOffer || false,
        category: categoryId
          ? categoryData?.name?.trim() || "Uncategorized"
          : Array.isArray(categoryData)
          ? categoryData.find((c) => c.id === design.categoryId)?.name?.trim() || "Uncategorized"
          : "Uncategorized",
        categoryId: design.categoryId || categoryId || "",
        driveLink: design.driveLink || "",
        code: design.code || "",
        sales: design.sales || 0,
      }));

      if (currentPage === 1) {
        setDesigns(newDesigns.sort((a, b) => b.sales - a.sales));
      } else {
        setDesigns((prev) => [...prev, ...newDesigns].sort((a, b) => b.sales - a.sales));
      }

      setTotalDesigns(designData.totalCount || 0);
      setHasMore(currentPage * pageSize < (designData.totalCount || 0));
      setIsLoadingMore(false);
    }
  }, [designData, categoryData, categoryId, currentPage]);

  const handleImageChange = (e, field, setState, state) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setState({
          ...state,
          [field]: reader.result.split(",")[1],
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e, setState, state) => {
    const { name, value, type, checked } = e.target;
    setState({
      ...state,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    setCreateError(null);
    try {
      const created = await createDesign({
        ...newDesign,
        newPrice: newDesign.newPrice || 180,
        oldPrice: newDesign.oldPrice || 200,
        code: newDesign.code || "None",
      });
      await queryClient.invalidateQueries({
        queryKey: ["designs", categoryId || "all"],
      });
      setNewDesign(null);
      toast.success("تم إضافة التصميم بنجاح!");
    } catch (err) {
      console.error("Error creating design:", err);
      setCreateError(err.message || "فشل في إضافة التصميم.");
      toast.error("فشل في إضافة التصميم!");
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateDesign(editingDesign.id, editingDesign);
      await queryClient.invalidateQueries({
        queryKey: ["designs", categoryId || "all"],
      });
      setEditingDesign(null);
      toast.success("تم تحديث التصميم بنجاح!");
    } catch (err) {
      console.error("Error updating design:", err);
      toast.error("فشل في تحديث التصميم!");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("هل أنت متأكد أنك تريد حذف هذا التصميم؟")) {
      try {
        await deleteDesign(id);
        setDesigns((prev) => prev.filter((d) => d.id !== id));
        toast.success("تم حذف التصميم بنجاح!");
      } catch (err) {
        toast.error("فشل في حذف التصميم!");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4" dir="rtl">
      <ToastContainer />
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold">
            إدارة التصميمات{" "}
            {categoryId && categoryData?.name ? `- ${categoryData.name}` : ""}
          </h1>
          <button
            onClick={() => navigate("/categories")}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            العودة
          </button>
        </div>

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">
            التصميمات ({designs.length} من {totalDesigns})
          </h2>
          <button
            onClick={() =>
              setNewDesign({
                categoryId: categoryId || "",
                name: "",
                imageBase64_1: "",
                imageBase64_2: "",
                oldPrice: 200,
                newPrice: 180,
                driveLink: "",
                inStock: true,
                hasOffer: false,
              })
            }
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            إضافة تصميم جديد
          </button>
        </div>

        {error && (
          <div className="bg-red-100 text-red-700 p-4 rounded mb-6">
            فشل في تحميل البيانات. حاول مرة أخرى.
          </div>
        )}

        {isLoading && currentPage === 1 ? (
          <SkeletonLoader type="table" />
        ) : designs.length > 0 ? (
          <>
            <div className="overflow-x-auto bg-white shadow-md rounded-lg">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-3 text-center"># ID</th>
                    <th className="p-3 text-right">الصورة</th>
                    <th className="p-3 text-right">الاسم</th>
                    {/* <th className="p-3 text-right">الفئة</th> */}
                    <th className="p-3 text-right">الكود</th>
                    {/* <th className="p-3 text-right">السعر</th> */}
                    <th className="p-3 text-right">المبيعات</th>
                    <th className="p-3 text-right">الحالة</th>
                    <th className="p-3 text-right">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {designs.map((design) => (
                    <tr key={design.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-center font-mono">{design.id}</td>
                      <td className="p-3 text-center">
                        {design.imageUrl_1 ? (
                          <img
                            src={`${API_BASE_URL_V2}${design.imageUrl_1}`}
                            alt={design.name}
                            className="h-16 w-16 object-cover rounded mx-auto"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-16 w-16 bg-gray-200 rounded mx-auto flex items-center justify-center">
                            لا توجد صورة
                          </div>
                        )}
                      </td>
                      <td className="p-3">{design.name}</td>
                      {/* <td className="p-3">{design.category}</td> */}
                      <td className="p-3">{design.code}</td>
                      {/* <td className="p-3">
                        {design.oldPrice > design.newPrice && (
                          <span className="line-through text-gray-500 mr-2">
                            {design.oldPrice}
                          </span>
                        )}
                        <span className="font-bold text-green-600">
                          {design.newPrice} جنيه
                        </span>
                      </td> */}
                      <td className="p-3 text-center">{design.sales}</td>
                      <td className="p-3 text-center">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            design.inStock
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {design.inStock ? "متوفر" : "غير متوفر"}
                        </span>
                        {design.hasOffer && (
                          <span className="block mt-1 text-orange-600 text-xs">
                            عرض خاص
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={() => setEditingDesign(design)}
                            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-sm"
                          >
                            تعديل
                          </button>
                          <button
                            onClick={() => handleDelete(design.id)}
                            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-sm"
                          >
                            حذف
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Load More Button */}
            <div className="text-center mt-10 mb-8">
              {hasMore && (
                <button
                  onClick={loadMoreDesigns}
                  disabled={isFetching}
                  className="bg-blue-600 text-white px-10 py-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-lg shadow-lg"
                >
                  {isFetching ? "جاري تحميل المزيد..." : "تحميل المزيد"}
                </button>
              )}

              {!hasMore && totalDesigns > 0 && (
                <p className="text-green-600 font-bold text-xl mt-6">
                  ✓ تم تحميل جميع التصميمات ({totalDesigns})
                </p>
              )}
            </div>
          </>
        ) : (
          <p className="text-center text-gray-600 py-10">لا توجد تصميمات</p>
        )}
        {newDesign && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
              <h2 className="text-xl font-semibold mb-4" dir="rtl">
                إضافة تصميم جديد
              </h2>
              {createError && (
                <div className="bg-red-100 text-red-700 p-4 rounded mb-4">
                  {createError}
                </div>
              )}
              <form onSubmit={handleCreate} dir="rtl">
                <div className="mb-4">
                  <label className="block text-right mb-2">الفئة</label>
                  <select
                    name="categoryId"
                    value={newDesign.categoryId}
                    onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                    className="w-full p-2 border rounded"
                    required
                  >
                    <option value="">اختر الفئة</option>
                    {(Array.isArray(categoryData) ? categoryData : [categoryData]).map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">اسم التصميم</label>
                  <input
                    type="text"
                    name="name"
                    value={newDesign.name}
                    onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">الصورة 1</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, "imageBase64_1", setNewDesign, newDesign)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">الصورة 2</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, "imageBase64_2", setNewDesign, newDesign)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">السعر قبل الخصم</label>
                  <input
                    type="number"
                    name="oldPrice"
                    value={newDesign.oldPrice}
                    onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">السعر بعد الخصم</label>
                  <input
                    type="number"
                    name="newPrice"
                    value={newDesign.newPrice}
                    onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">رابط درايف</label>
                  <input
                    type="text"
                    name="driveLink"
                    value={newDesign.driveLink}
                    onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">الكود</label>
                  <input
                    type="text"
                    name="code"
                    value={newDesign.code}
                    onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">
                    <input
                      type="checkbox"
                      name="inStock"
                      checked={newDesign.inStock}
                      onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                      className="ml-2"
                    />
                    في المخزون
                  </label>
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">
                    <input
                      type="checkbox"
                      name="hasOffer"
                      checked={newDesign.hasOffer}
                      onChange={(e) => handleInputChange(e, setNewDesign, newDesign)}
                      className="ml-2"
                    />
                    عرض خاص
                  </label>
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setNewDesign(null)}
                    className="bg-gray-300 text-black py-2 px-4 rounded hover:bg-gray-400"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="bg-orange-500 text-white py-2 px-4 rounded hover:bg-orange-600 flex items-center gap-2"
                    style={{ backgroundColor: colors.orange }}
                    disabled={isCreating}
                  >
                    {isCreating ? (
                      <svg className="animate-spin h-5 w-5 text-white opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    ) : null}
                    {isCreating ? 'جاري الإضافة...' : 'إضافة'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {editingDesign && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
              <h2 className="text-xl font-semibold mb-4" dir="rtl">
                تعديل التصميم #{editingDesign.id}
              </h2>
              <form onSubmit={handleUpdate} dir="rtl">
                <div className="mb-4">
                  <label className="block text-right mb-2">الفئة</label>
                  <select
                    name="categoryId"
                    value={editingDesign.categoryId}
                    onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                    className="w-full p-2 border rounded"
                    required
                  >
                    <option value="">اختر الفئة</option>
                    {(Array.isArray(categoryData) ? categoryData : [categoryData]).map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">اسم التصميم</label>
                  <input
                    type="text"
                    name="name"
                    value={editingDesign.name}
                    onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">الصورة 1 الحالية</label>
                  {editingDesign.imageUrl_1 && (
                    <img
                      src={`${API_BASE_URL_V2}${editingDesign.imageUrl_1}`}
                      alt={editingDesign.name ? editingDesign.name : "تصميم"}
                      className="h-20 object-cover rounded mb-2"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, "imageBase64_1", setEditingDesign, editingDesign)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">الصورة 2 الحالية</label>
                  {editingDesign.imageUrl_2 && (
                    <img
                      src={`${API_BASE_URL_V2}${editingDesign.imageUrl_2}`}
                      alt={editingDesign.name ? editingDesign.name : "تصميم"}
                      className="h-20 object-cover rounded mb-2"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, "imageBase64_2", setEditingDesign, editingDesign)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">الكود</label>
                  <input
                    type="text"
                    name="code"
                    value={editingDesign.code}
                    readOnly
                    className="w-full p-2 border rounded bg-gray-100 cursor-not-allowed"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">السعر قبل الخصم</label>
                  <input
                    type="number"
                    name="oldPrice"
                    value={editingDesign.oldPrice}
                    onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">السعر بعد الخصم</label>
                  <input
                    type="number"
                    name="newPrice"
                    value={editingDesign.newPrice}
                    onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">رابط درايف</label>
                  <input
                    type="text"
                    name="driveLink"
                    value={editingDesign.driveLink}
                    onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">
                    <input
                      type="checkbox"
                      name="inStock"
                      checked={editingDesign.inStock}
                      onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                      className="ml-2"
                    />
                    في المخزون
                  </label>
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">
                    <input
                      type="checkbox"
                      name="hasOffer"
                      checked={editingDesign.hasOffer}
                      onChange={(e) => handleInputChange(e, setEditingDesign, editingDesign)}
                      className="ml-2"
                    />
                    عرض خاص
                  </label>
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setEditingDesign(null)}
                    className="bg-gray-300 text-black py-2 px-4 rounded hover:bg-gray-400"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="bg-orange-500 text-white py-2 px-4 rounded hover:bg-orange-600"
                    style={{ backgroundColor: colors.orange }}
                  >
                    حفظ
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DesignsPage;
